import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { MusicalWorkUpdateStore } from "../../store";
import { runInAction } from "mobx";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { WorkAuthorUpdate } from "../../../workAuthor/components/update/WorkAuthorUpdate";
import { WorkAuthorUpdateStore } from "../../../workAuthor/store";
import { GridColumn } from "../../../common/types";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { InterpreterCreate } from "../../../interpreter/components/create/InterpreterCreate";
import { InterpreterCreateStore } from "../../../interpreter/store";
import { TerritoryCreate } from "../../../territory/components/create/TerritoryCreate";
import { TerritoryCreateStore } from "../../../territory/store";
import { MusicalWorkIswcCustomField } from "../custom/MusicalWorkIswcCustomField";
import { FileCRUDType } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { PhonogramUpdate } from "../../../phonogram/components/update/PhonogramUpdate";
import { PhonogramUpdateStore } from "../../../phonogram/store";
import { InputHtmlString } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import {
  SharesPieChart,
  computeSharesFromCrud,
} from "../../../../common/components/SharesPieChart";

export type MusicalWorkUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  title: string;
  titleSelectedValue?: { displayId: string; id: string } | null;
  onTitleChange?: (value: string) => void;
  titleError?: string;
  musicalGenreSelectedValue?: { displayId: string; id: string } | null;
  onMusicalGenreChange?: (value: string) => void;
  onMusicalGenreSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  musicalGenreOptions?: { displayId: string; id: string }[];
  musicalGenreError?: string;
  alternativeTitles: string[];
  alternativeTitlesSelectedValue?: { displayId: string; id: string } | null;
  onAlternativeTitlesChange?: (value: string[]) => void;
  alternativeTitlesError?: string;
  authorsOnRestore: (id: string) => void;
  authorsOnCommitChanges: (changes: GridChangeSet) => void;
  authorsOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  authorsValidateChanges: (changes: GridChangeSet) => boolean;
  authorsRows: CrudRow[];
  authorsError?: string;
  workAuthorUpdateStore: WorkAuthorUpdateStore;
  authorsVisibleColumns: GridColumn[];
  authorsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  notes: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notesError?: string;
  performersSelectedValues: { displayId: string; id: string }[];
  onPerformersChange?: (value: string) => void;
  onPerformersSelected?: (value: { displayId: string; id: string }[]) => void;
  performersOptions?: { displayId: string; id: string }[];
  performersError?: string;
  cmsId: string;
  cmsIdSelectedValue?: { displayId: string; id: string } | null;
  onCmsIdChange?: (value: string) => void;
  cmsIdError?: string;
  territoriesSelectedValues: { displayId: string; id: string }[];
  onTerritoriesChange?: (value: string) => void;
  onTerritoriesSelected?: (value: { displayId: string; id: string }[]) => void;
  territoriesOptions?: { displayId: string; id: string }[];
  territoriesError?: string;
  iswc: string;
  iswcSelectedValue?: { displayId: string; id: string } | null;
  onIswcChange?: (value: string) => void;
  iswcError?: string;
  scoreOnRestoreFile: (id: string) => void;
  scoreOnLoadFile: (file: any) => void;
  scoreOnCommitFile: (changes: GridChangeSet) => void;
  scoreFiles: FileCRUDType[];
  scoreError?: string;
  phonogramsOnRestore: (id: string) => void;
  phonogramsOnCommitChanges: (changes: GridChangeSet) => void;
  phonogramsOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  phonogramsValidateChanges: (changes: GridChangeSet) => boolean;
  phonogramsRows: CrudRow[];
  phonogramsError?: string;
  phonogramUpdateStore: PhonogramUpdateStore;
  phonogramsVisibleColumns: GridColumn[];
  phonogramsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  originalLyrics: string;
  originalLyricsSelectedValue?: { displayId: string; id: string } | null;
  onOriginalLyricsChange?: (value: string) => void;
  originalLyricsError?: string;
  contractObservation: string;
  contractObservationSelectedValue?: { displayId: string; id: string } | null;
  onContractObservationChange?: (value: string) => void;
  contractObservationError?: string;
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
  dummyCRUDRestoredObservable: number;
  musicalWorkUpdateStore: MusicalWorkUpdateStore;
  authorsForChart?: any[];
};

export const MusicalWorkUpdateForm: React.FC<MusicalWorkUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      recordId,
      title = "",
      titleError,
      onTitleChange = () => {},
      onMusicalGenreSelected = () => {},
      musicalGenreOptions = [],
      musicalGenreSelectedValue = null,
      musicalGenreError,
      onMusicalGenreChange = () => {},
      alternativeTitles = [],
      alternativeTitlesError,
      onAlternativeTitlesChange = () => {},
      authorsOnRestore,
      authorsOnCommitChanges,
      authorsOnCommitInlineChange,
      authorsValidateChanges,
      authorsRows,
      authorsError,
      workAuthorUpdateStore,
      authorsVisibleColumns,
      authorsColumnTypes,
      notes = "",
      notesError,
      onNotesChange = () => {},
      onPerformersSelected = () => {},
      performersOptions = [],
      performersSelectedValues,
      performersError,
      onPerformersChange = () => {},
      cmsId = "",
      cmsIdError,
      onCmsIdChange = () => {},
      onTerritoriesSelected = () => {},
      territoriesOptions = [],
      territoriesSelectedValues,
      territoriesError,
      onTerritoriesChange = () => {},
      iswc = "",
      iswcError,
      onIswcChange = () => {},
      scoreOnRestoreFile,
      scoreOnLoadFile,
      scoreOnCommitFile,
      scoreFiles,
      scoreError,
      phonogramsOnRestore,
      phonogramsOnCommitChanges,
      phonogramsOnCommitInlineChange,
      phonogramsValidateChanges,
      phonogramsRows,
      phonogramsError,
      phonogramUpdateStore,
      phonogramsVisibleColumns,
      phonogramsColumnTypes,
      originalLyrics = "",
      originalLyricsError,
      onOriginalLyricsChange = () => {},
      contractObservation = "",
      contractObservationError,
      onContractObservationChange = () => {},
      createdAt_ = null,
      lastUpdatedAt_ = null,
      dummyCRUDRestoredObservable,
      musicalWorkUpdateStore,
      authorsForChart = [],
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      if (workAuthorUpdateStore.status === "uninitialized") {
        workAuthorUpdateStore.initialize();
      }

      if (phonogramUpdateStore.status === "uninitialized") {
        phonogramUpdateStore.initialize();
      }

      const getViewAction = (
        onDownload: (props: {
          id: string;
          type: string;
          name: string;
          __status__: RowCrudStatus;
        }) => void,
        onViewPdf?: (props: any) => void
      ) => {
        const actions: any[] = [];
        if (onViewPdf) {
          actions.push({
            columnName: "__action_view_pdf",
            onClick: (props: any) =>
              isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
            icon: <VisibilityIcon />,
            label: langStore.viewCommand,
            isActionEnabled: (props: any) =>
              props.name?.toLowerCase().endsWith(".pdf"),
          });
        }
        actions.push({
          columnName: "__action_view",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
          icon: <DownloadIcon />,
          label: langStore.downloadCommand,
        });
        return actions;
      };
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              recordId={recordId}
              values={{
                title,
                musicalGenre: musicalGenreSelectedValue,
                alternativeTitles,
                notes,
                cmsId,
                iswc,
                originalLyrics,
                contractObservation,
                createdAt_,
                lastUpdatedAt_,
              }}
            >
              <FormExtras
                entity="MusicalWork"
                mode="update"
                recordId={recordId}
                values={{
                  title,
                  musicalGenre: musicalGenreSelectedValue,
                  alternativeTitles,
                  notes,
                  cmsId,
                  iswc,
                  originalLyrics,
                  contractObservation,
                  createdAt_,
                  lastUpdatedAt_,
                }}
                options={{ musicalGenre: musicalGenreOptions }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "title":
                      onTitleChange?.(valor);
                      break;
                    case "musicalGenre":
                      onMusicalGenreSelected?.(valor);
                      break;
                    case "alternativeTitles":
                      onAlternativeTitlesChange?.(valor);
                      break;
                    case "notes":
                      onNotesChange?.(valor);
                      break;
                    case "cmsId":
                      onCmsIdChange?.(valor);
                      break;
                    case "iswc":
                      onIswcChange?.(valor);
                      break;
                    case "originalLyrics":
                      onOriginalLyricsChange?.(valor);
                      break;
                    case "contractObservation":
                      onContractObservationChange?.(valor);
                      break;
                  }
                }}
              />

              <GroupContainer
                id="workInfo"
                errorText={musicalWorkUpdateStore.getCardErrors("workInfo")}
                title={langStore.groupMusicalWorkWorkInfoLabel}
                collapsible={true}
                expanded={musicalWorkUpdateStore.getCardState("workInfo")}
                toggleExpand={() =>
                  musicalWorkUpdateStore.toggleCardState("workInfo")
                }
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ flex: "0 0 400px" }}>
                    <FormControl fullWidth>
                      <InputString
                        autoFocus
                        value={title}
                        error={Boolean(titleError)}
                        warning={
                          !titleError &&
                          Boolean(musicalWorkUpdateStore.warnings["title"])
                        }
                        helperText={
                          titleError ||
                          musicalWorkUpdateStore.warnings["title"]?.message
                        }
                        label={langStore.MusicalWorkTitleLabel}
                        onChange={(value: string) => onTitleChange(value)}
                        description={langStore.MusicalWorkTitleDescription}
                      />
                      <InputReference
                        id="musicalGenre"
                        label={langStore.MusicalWorkMusicalGenreLabel}
                        error={Boolean(musicalGenreError)}
                        helperText={
                          musicalGenreError ||
                          musicalWorkUpdateStore.warnings["musicalGenre"]?.message
                        }
                        selectedValue={musicalGenreSelectedValue}
                        onChange={(value: string) => onMusicalGenreChange(value)}
                        onSelect={(
                          value: { displayId: string; id: string } | null
                        ) => onMusicalGenreSelected(value)}
                        options={musicalGenreOptions}
                        description={langStore.MusicalWorkMusicalGenreDescription}
                      />
                      <InputStringList
                        value={alternativeTitles}
                        error={Boolean(alternativeTitlesError)}
                        helperText={
                          alternativeTitlesError ||
                          musicalWorkUpdateStore.warnings["alternativeTitles"]
                            ?.message
                        }
                        label={langStore.MusicalWorkAlternativeTitlesLabel}
                        description={
                          langStore.MusicalWorkAlternativeTitlesDescription
                        }
                        onChange={(value: string[]) =>
                          onAlternativeTitlesChange(value)
                        }
                      />
                      <ItemsCRUDCompact
                        label={langStore.MusicalWorkAuthorsLabel}
                        description={langStore.MusicalWorkAuthorsDescription}
                        showLabel={true}
                        rows={authorsRows}
                        columns={authorsVisibleColumns}
                        columnTypes={authorsColumnTypes}
                        canCreate={true}
                        isEditVisible={() =>
                          rootStore.authStore.hasPermissions("workAuthor_update")
                        }
                        isDeleteVisible={() =>
                          rootStore.authStore.hasPermissions("workAuthor_delete")
                        }
                        popupComponent={(row: any) => {
                          if (
                            row &&
                            row.id &&
                            !(
                              typeof row.id === "string" &&
                              row.id.startsWith("auto-")
                            )
                          ) {
                            runInAction(() =>
                              musicalWorkUpdateStore.authorsLoadRow(row)
                            );
                          }
                          return (
                            <WorkAuthorUpdate
                              workAuthorUpdateStore={workAuthorUpdateStore}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          );
                        }}
                        popupMaxWidth="xl"
                        onRestore={(id: string) => authorsOnRestore(id)}
                        onCommitChanges={(changes: GridChangeSet) =>
                          authorsOnCommitChanges(changes)
                        }
                        onCommitInlineChange={(
                          rowId: string,
                          changes: Record<string, any>
                        ) => authorsOnCommitInlineChange(rowId, changes)}
                        validate={(changes: GridChangeSet) =>
                          authorsValidateChanges(changes)
                        }
                        dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                        error={authorsError}
                      />
                      <InputText
                        value={notes}
                        error={Boolean(notesError)}
                        warning={
                          !notesError &&
                          Boolean(musicalWorkUpdateStore.warnings["notes"])
                        }
                        helperText={
                          notesError ||
                          musicalWorkUpdateStore.warnings["notes"]?.message
                        }
                        label={langStore.MusicalWorkNotesLabel}
                        onChange={(value: string) => onNotesChange(value)}
                        nullText={langStore.optionalFieldHint}
                        description={langStore.MusicalWorkNotesDescription}
                      />
                    </FormControl>
                  </Box>
                  {authorsForChart.length > 0 && (
                    <Box sx={{ flex: "1 1 400px", minWidth: 400 }}>
                      <SharesPieChart
                        data={computeSharesFromCrud(authorsForChart)}
                        title="Distribución Autores y Editoriales"
                        subtitle="Participación neta de autores y editoriales"
                      />
                    </Box>
                  )}
                </Box>
              </GroupContainer>

              <GroupContainer
                id="workRegistration"
                errorText={musicalWorkUpdateStore.getCardErrors(
                  "workRegistration"
                )}
                title={langStore.groupMusicalWorkWorkRegistrationLabel}
                collapsible={true}
                expanded={musicalWorkUpdateStore.getCardState(
                  "workRegistration"
                )}
                toggleExpand={() =>
                  musicalWorkUpdateStore.toggleCardState("workRegistration")
                }
              >
                <FormControl fullWidth>
                  <InputReferenceSet
                    id="performers"
                    label={langStore.MusicalWorkPerformersLabel}
                    error={Boolean(performersError)}
                    helperText={
                      performersError ||
                      musicalWorkUpdateStore.warnings["performers"]?.message
                    }
                    selectedValues={performersSelectedValues}
                    onChange={(value: string) => onPerformersChange(value)}
                    onSelect={(value: { displayId: string; id: string }[]) =>
                      onPerformersSelected(value)
                    }
                    options={performersOptions}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.MusicalWorkPerformersDescription}
                    canCreate={rootStore.authStore.hasPermissions(
                      "interpreter_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new InterpreterCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <InterpreterCreate
                            interpreterCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewInterpreter"
                        cancelMethod="cancelNewInterpreter"
                      />
                    )}
                  />
                  <InputString
                    value={cmsId}
                    error={Boolean(cmsIdError)}
                    warning={
                      !cmsIdError &&
                      Boolean(musicalWorkUpdateStore.warnings["cmsId"])
                    }
                    helperText={
                      cmsIdError ||
                      musicalWorkUpdateStore.warnings["cmsId"]?.message
                    }
                    label={langStore.MusicalWorkCmsIdLabel}
                    onChange={(value: string) => onCmsIdChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.MusicalWorkCmsIdDescription}
                  />
                  <InputReferenceSet
                    id="territories"
                    label={langStore.MusicalWorkTerritoriesLabel}
                    error={Boolean(territoriesError)}
                    helperText={
                      territoriesError ||
                      musicalWorkUpdateStore.warnings["territories"]?.message
                    }
                    selectedValues={territoriesSelectedValues}
                    onChange={(value: string) => onTerritoriesChange(value)}
                    onSelect={(value: { displayId: string; id: string }[]) =>
                      onTerritoriesSelected(value)
                    }
                    options={territoriesOptions}
                    description={langStore.MusicalWorkTerritoriesDescription}
                    pinnedDisplayIds={["Todo el mundo"]}
                    canCreate={rootStore.authStore.hasPermissions(
                      "territory_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new TerritoryCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <TerritoryCreate
                            territoryCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewTerritory"
                        cancelMethod="cancelNewTerritory"
                      />
                    )}
                  />
                  <MusicalWorkIswcCustomField
                    value={iswc}
                    error={Boolean(iswcError)}
                    helperText={
                      iswcError ||
                      musicalWorkUpdateStore.warnings["iswc"]?.message
                    }
                    {...{ optional: true }}
                    label={langStore.MusicalWorkIswcLabel}
                    description={langStore.MusicalWorkIswcDescription}
                    onChange={(value: string) => onIswcChange(value)}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="workContent"
                errorText={musicalWorkUpdateStore.getCardErrors("workContent")}
                title={langStore.groupMusicalWorkWorkContentLabel}
                collapsible={true}
                expanded={musicalWorkUpdateStore.getCardState("workContent")}
                toggleExpand={() =>
                  musicalWorkUpdateStore.toggleCardState("workContent")
                }
              >
                <FormControl fullWidth>
                  <LoadFilesCRUDCompact
                    rows={scoreFiles}
                    maxRows={1}
                    acceptFileTypes=".pdf"
                    menuActions={getViewAction(
                      (props) =>
                        musicalWorkUpdateStore.fileDownload(props, "score"),
                      (props) =>
                        musicalWorkUpdateStore.viewFilePdf(props, "score")
                    )}
                    showAddCommand={true}
                    showDeleteCommand={true}
                    label={langStore.MusicalWorkScoreLabel}
                    description={langStore.MusicalWorkScoreDescription}
                    onLoadFile={(file: File) => scoreOnLoadFile(file)}
                    columns={[
                      { name: "mimetype", title: langStore.fileTypeLabel },
                      { name: "name", title: langStore.fileNameLabel },
                      { name: "formattedSize", title: langStore.fileSizeLabel },
                    ]}
                    onRestore={(id: string) => scoreOnRestoreFile(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      scoreOnCommitFile(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={scoreError}
                  />
                  <ItemsCRUDCompact
                    label={langStore.MusicalWorkPhonogramsLabel}
                    description={langStore.MusicalWorkPhonogramsDescription}
                    showLabel={true}
                    rows={phonogramsRows}
                    columns={phonogramsVisibleColumns}
                    columnTypes={phonogramsColumnTypes}
                    canCreate={true}
                    isEditVisible={() =>
                      rootStore.authStore.hasPermissions("phonogram_update")
                    }
                    isDeleteVisible={() =>
                      rootStore.authStore.hasPermissions("phonogram_delete")
                    }
                    popupComponent={(row: any) => {
                      if (
                        row &&
                        row.id &&
                        !(
                          typeof row.id === "string" &&
                          row.id.startsWith("auto-")
                        )
                      ) {
                        runInAction(() =>
                          musicalWorkUpdateStore.phonogramsLoadRow(row)
                        );
                      }
                      return (
                        <PhonogramUpdate
                          phonogramUpdateStore={phonogramUpdateStore}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      );
                    }}
                    popupMaxWidth="xl"
                    onRestore={(id: string) => phonogramsOnRestore(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      phonogramsOnCommitChanges(changes)
                    }
                    onCommitInlineChange={(
                      rowId: string,
                      changes: Record<string, any>
                    ) => phonogramsOnCommitInlineChange(rowId, changes)}
                    validate={(changes: GridChangeSet) =>
                      phonogramsValidateChanges(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={phonogramsError}
                  />
                  <InputText
                    value={originalLyrics}
                    error={Boolean(originalLyricsError)}
                    warning={
                      !originalLyricsError &&
                      Boolean(musicalWorkUpdateStore.warnings["originalLyrics"])
                    }
                    helperText={
                      originalLyricsError ||
                      musicalWorkUpdateStore.warnings["originalLyrics"]?.message
                    }
                    label={langStore.MusicalWorkOriginalLyricsLabel}
                    onChange={(value: string) => onOriginalLyricsChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.MusicalWorkOriginalLyricsDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="contracts"
                errorText={musicalWorkUpdateStore.getCardErrors("contracts")}
                title={langStore.groupMusicalWorkContractsLabel}
                collapsible={true}
                expanded={musicalWorkUpdateStore.getCardState("contracts")}
                toggleExpand={() =>
                  musicalWorkUpdateStore.toggleCardState("contracts")
                }
              >
                <FormControl fullWidth>
                  <InputHtmlString
                    value={contractObservation}
                    error={Boolean(contractObservationError)}
                    helperText={
                      contractObservationError ||
                      musicalWorkUpdateStore.warnings["contractObservation"]
                        ?.message
                    }
                    label={langStore.MusicalWorkContractObservationLabel}
                    onChange={(value: string) =>
                      onContractObservationChange(value)
                    }
                    fragment={true}
                    description={
                      langStore.MusicalWorkContractObservationDescription
                    }
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="systemDates"
                errorText={musicalWorkUpdateStore.getCardErrors("systemDates")}
                title={langStore.groupMusicalWorkSystemDatesLabel}
                collapsible={true}
                expanded={musicalWorkUpdateStore.getCardState("systemDates")}
                toggleExpand={() =>
                  musicalWorkUpdateStore.toggleCardState("systemDates")
                }
              >
                <FormControl fullWidth>
                  <ShowDateTime
                    label={langStore.MusicalWorkCreatedAt_Label}
                    description={langStore.MusicalWorkCreatedAt_Description}
                    value={createdAt_}
                  />
                  <ShowDateTime
                    label={langStore.MusicalWorkLastUpdatedAt_Label}
                    description={langStore.MusicalWorkLastUpdatedAt_Description}
                    value={lastUpdatedAt_}
                  />
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
          <PDFViewerDialog
            open={musicalWorkUpdateStore.pdfViewerOpen}
            onClose={() => musicalWorkUpdateStore.closePdfViewer()}
            pdfUrl={musicalWorkUpdateStore.pdfViewerUrl}
            title={musicalWorkUpdateStore.pdfViewerTitle}
          />
        </Grid>
      );
    }
  );
